import React from 'react';
import { apiClient } from '../../../lib/apiClient';

/**
 * CTV-457: fetches a short-lived signed view URL for a previously-uploaded
 * commercial whose storagePath we know (because the user uploaded it in a
 * prior session or via Rerun) but which has no `selfservice_creatives` row
 * yet. Used by the wizard's Step 3 to render the actual video instead of a
 * generic "already uploaded" banner.
 *
 * Returns `{ url, posterUrl, loading }`. `url` is the signed video URL; null
 * while loading or after a failure. CTV-508: `posterUrl` is the signed
 * server-generated poster still when one exists, else null (creatives uploaded
 * before posters, or whose ffmpeg step failed, have none — callers fall back to
 * the `#t=0.5` <video> seek). `loading` flips to false either way so the caller
 * can fall back to the dropzone if needed.
 */
export function useCreativePreviewUrl(
  slug: string | undefined,
  storagePath: string | null | undefined,
): { url: string | null; posterUrl: string | null; loading: boolean } {
  const [url, setUrl] = React.useState<string | null>(null);
  const [posterUrl, setPosterUrl] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!slug || !storagePath) {
      setUrl(null);
      setPosterUrl(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setUrl(null);
    setPosterUrl(null);
    apiClient
      .post<{ url: string; posterUrl?: string | null }>('/creatives/preview-url', { slug, storagePath })
      .then(({ url: signed, posterUrl: poster }) => {
        if (!cancelled) {
          setUrl(signed);
          setPosterUrl(poster ?? null);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setUrl(null);
          setPosterUrl(null);
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [slug, storagePath]);

  return { url, posterUrl, loading };
}
